import { useEffect, useRef } from "react";

/**
 * Sora-2 talking-head loop player.
 *
 * Renders an absolutely-positioned <video> overlay that:
 *   - Plays continuously (looping) when `speaking` is true — gives true lip-sync vibe
 *     because the Sora-generated face is naturally moving its mouth.
 *   - Pauses (still frame visible) when `speaking` is false — shows the gemologist
 *     in a calm pose. We keep the video visible at all times (not the still avatar)
 *     so there's no face-mismatch flash between two different generated portraits.
 */
const TalkingHeadVideo = ({ videoUrl, speaking }) => {
  const ref = useRef(null);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    if (speaking) {
      v.currentTime = 0;
      v.play().catch((err) => {
        // Browser autoplay policy may reject — log to console but don't surface to user.
        // The voice still plays; only the looped video face is missed.
        if (process.env.NODE_ENV !== "production") console.warn("Talking-head video autoplay blocked:", err?.message);
      });
    } else {
      try { v.pause(); } catch (err) {
        if (process.env.NODE_ENV !== "production") console.warn("Talking-head video pause failed:", err?.message);
      }
    }
  }, [speaking]);

  if (!videoUrl) return null;
  return (
    <video
      ref={ref}
      src={videoUrl}
      muted
      loop
      playsInline
      preload="auto"
      data-testid="talking-head-video"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ pointerEvents: "none" }}
    />
  );
};

export default TalkingHeadVideo;
